/**
 * RTDBListenerManager - Persistent RTDB Listener Manager
 *
 * Manages Firebase Realtime Database listeners with lazy-loading and session persistence.
 * Unlike the standard useRealtimeDatabase hook which unsubscribes on path change,
 * this manager keeps listeners alive for the entire session.
 *
 * Benefits:
 * - Switching locations uses cached data (no full re-sync)
 * - Delta updates are ~100x cheaper than full syncs
 * - Power users accumulate listeners as they visit locations
 * - Casual users only subscribe to locations they actually visit
 *
 * Usage:
 * - Initialize once in AuthenticatedLayout with RTDB instance
 * - Call subscribe() when user visits a location
 * - Data persists until tab closes or cleanup() is called
 */

import { ref, onValue, Database, Unsubscribe } from 'firebase/database';
import { logger } from '../../utils/logger';
import { computeSnapshotTelemetry } from './rtdbSnapshotTelemetry';

export type RTDBDataCallback<T> = (data: T[], locationId: string) => void;
type RTDBParseFunction<T> = (data: unknown) => T[];

interface ListenerEntry<T> {
  unsubscribe: Unsubscribe;
  locationId: string;
  path: string;
  data: T[];
  lastUpdated: number;
}

interface ManagerConfig<T> {
  /** Function to generate RTDB path from locationId */
  getPath: (locationId: string) => string;
  /** Parse raw RTDB data into typed array */
  parseData: RTDBParseFunction<T>;
  /** Callback when data changes for any location */
  onDataChange?: RTDBDataCallback<T>;
  /** Board name for logging */
  boardName: string;
}

/**
 * Creates a persistent RTDB listener manager for a specific board type.
 * Each board (rentals, stamps) should have its own manager instance.
 */
export class RTDBListenerManager<T> {
  private db: Database | null = null;
  private listeners = new Map<string, ListenerEntry<T>>();
  private config: ManagerConfig<T>;
  private dataChangeCallbacks = new Set<RTDBDataCallback<T>>();

  constructor(config: ManagerConfig<T>) {
    this.config = config;
  }

  /**
   * Initialize with Firebase RTDB instance.
   * Call this once when Firebase is ready.
   */
  initialize(db: Database): void {
    this.db = db;
    logger.log(`[${this.config.boardName}] RTDBListenerManager initialized`);
  }

  /**
   * Check if manager is initialized with a database
   */
  isInitialized(): boolean {
    return this.db !== null;
  }

  /**
   * Subscribe to a location's data. If already subscribed, returns cached data.
   * This is the key optimization - listeners persist across location changes.
   *
   * @param locationId - The location to subscribe to
   * @returns Current data for the location (may be empty if first load)
   */
  subscribe(locationId: string): T[] {
    if (!this.db) {
      logger.warn(`[${this.config.boardName}] Cannot subscribe - manager not initialized`);
      return [];
    }

    // Already subscribed? Return cached data
    if (this.listeners.has(locationId)) {
      const entry = this.listeners.get(locationId)!;
      logger.log(`[${this.config.boardName}] Using cached listener for location ${locationId}`);
      return entry.data;
    }

    // New subscription
    const path = this.config.getPath(locationId);
    logger.rtdb('subscribe', path);

    const dbRef = ref(this.db, path);

    // Tracks whether we've seen the first snapshot for THIS subscription's
    // lifecycle. Only that first snapshot (the initial sync) gets a full
    // JSON.stringify size — subsequent delta events skip the O(board-size)
    // stringify entirely (see rtdbSnapshotTelemetry.ts).
    let hasReceivedInitialSnapshot = false;

    const unsubscribe = onValue(
      dbRef,
      (snapshot) => {
        try {
          const rawData = snapshot.val();
          const isFirstSnapshot = !hasReceivedInitialSnapshot;
          hasReceivedInitialSnapshot = true;

          // Telemetry logging (full size on initial sync only; delta events are flagged, not re-stringified)
          const { exists, size, isInitialSync } = computeSnapshotTelemetry(rawData, isFirstSnapshot);
          logger.rtdb('snapshot', path, { exists, size, isInitialSync });

          // Parse the data
          const items = this.config.parseData(rawData);

          // Update stored data
          const entry = this.listeners.get(locationId);
          if (entry) {
            entry.data = items;
            entry.lastUpdated = Date.now();
          }

          // Notify all callbacks
          this.notifyDataChange(items, locationId);
        } catch (error) {
          logger.error(`[${this.config.boardName}] Error processing snapshot for ${locationId}:`, error);
        }
      },
      (error) => {
        logger.error(`[${this.config.boardName}] Listener error for ${locationId}:`, error);
      }
    );

    // Store the listener entry
    this.listeners.set(locationId, {
      unsubscribe,
      locationId,
      path,
      data: [],
      lastUpdated: Date.now(),
    });

    logger.log(`[${this.config.boardName}] Created new listener for location ${locationId} (total: ${this.listeners.size})`);

    return []; // Data will arrive via callback
  }

  /**
   * Get cached data for a location without subscribing.
   * Returns null if not subscribed to this location.
   */
  getCachedData(locationId: string): T[] | null {
    const entry = this.listeners.get(locationId);
    return entry ? entry.data : null;
  }

  /**
   * Check if we have an active listener for a location
   */
  hasListener(locationId: string): boolean {
    return this.listeners.has(locationId);
  }

  /**
   * Get all locations we're currently subscribed to
   */
  getActiveLocations(): string[] {
    return Array.from(this.listeners.keys());
  }

  /**
   * Register a callback for data changes.
   * The callback is called whenever ANY subscribed location receives new data.
   */
  addDataChangeCallback(callback: RTDBDataCallback<T>): void {
    this.dataChangeCallbacks.add(callback);
  }

  /**
   * Remove a data change callback
   */
  removeDataChangeCallback(callback: RTDBDataCallback<T>): void {
    this.dataChangeCallbacks.delete(callback);
  }

  /**
   * Unsubscribe from a specific location.
   * Generally not needed - listeners persist for session.
   */
  unsubscribe(locationId: string): void {
    const entry = this.listeners.get(locationId);
    if (entry) {
      logger.rtdb('unsubscribe', entry.path);
      entry.unsubscribe();
      this.listeners.delete(locationId);
      logger.log(`[${this.config.boardName}] Unsubscribed from location ${locationId} (remaining: ${this.listeners.size})`);
    }
  }

  /**
   * Clean up all listeners. Call this on app unmount.
   */
  cleanup(): void {
    logger.log(`[${this.config.boardName}] Cleaning up ${this.listeners.size} listeners`);
    this.listeners.forEach((entry) => {
      logger.rtdb('unsubscribe', entry.path);
      entry.unsubscribe();
    });
    this.listeners.clear();
    this.dataChangeCallbacks.clear();
    this.db = null;
  }

  /**
   * Get stats about active listeners (useful for debugging/telemetry)
   */
  getStats(): { locationCount: number; locations: string[] } {
    return {
      locationCount: this.listeners.size,
      locations: Array.from(this.listeners.keys()),
    };
  }

  private notifyDataChange(data: T[], locationId: string): void {
    // Notify config callback
    if (this.config.onDataChange) {
      this.config.onDataChange(data, locationId);
    }

    // Notify registered callbacks
    this.dataChangeCallbacks.forEach((callback) => {
      try {
        callback(data, locationId);
      } catch (error) {
        logger.error(`[${this.config.boardName}] Error in data change callback:`, error);
      }
    });
  }
}
